import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Download, ShieldCheck } from "lucide-react";
import { http, formatDetail, openSignedDownload, auDateTime } from "./api";

export default function OutputsTab({ engagementId }) {
  const [items, setItems] = useState(null);
  const [ackFor, setAckFor] = useState(null);
  const [agree, setAgree] = useState(false);

  const load = useCallback(() => {
    http.get(`/portal/client/engagements/${engagementId}/outputs`)
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Could not load outputs."));
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);

  const download = async (o) => {
    if (!o.acknowledged) { setAckFor(o); setAgree(false); return; }
    try {
      await openSignedDownload(`/portal/client/outputs/${o.id}/download-link`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    }
  };

  const confirmAck = async () => {
    try {
      await http.post(`/portal/client/outputs/${ackFor.id}/acknowledge`);
      toast.success("Licence acknowledged.");
      await openSignedDownload(`/portal/client/outputs/${ackFor.id}/download-link`);
      setAckFor(null);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="outputs-tab">
      <h2 className="font-serif text-2xl mb-6">Approved outputs</h2>
      {items === null ? (
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone2 border border-line bg-surface1 p-8" data-testid="outputs-empty">No approved outputs have been released yet.</p>
      ) : (
        <div className="space-y-px bg-line/40 border border-line/40">
          {items.map((o, i) => (
            <article key={o.id} data-testid={`output-row-${i}`} className="bg-ink p-6 md:p-8">
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                <h3 className="font-serif text-xl">{o.title}</h3>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${o.status === "Expired" ? "border-line text-stone2" : "border-ember/50 text-ember"}`}>{o.status === "Expired" ? "Expired" : "Final output available"}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 font-mono text-[11px] text-stone2 mb-4">
                <span>Document ID: <span className="text-bone">{o.document_id}</span></span>
                <span>Version: <span className="text-bone">{o.version}</span></span>
                <span>Category: <span className="text-bone">{o.category}</span></span>
                <span>Approved recipient: <span className="text-bone">{o.approved_recipient}</span></span>
                <span>Issued: <span className="text-bone">{o.issue_date || "—"}</span></span>
                <span>{o.expiry_date ? <>Expiry: <span className="text-bone">{o.expiry_date}</span></> : o.review_date ? <>Review: <span className="text-bone">{o.review_date}</span></> : null}</span>
                <span>Watermark: <span className="text-bone">{o.watermark_id}</span></span>
                <span className="lg:col-span-2">Permitted purpose: <span className="text-bone">{o.permitted_purpose}</span></span>
              </div>
              <p className="text-sm text-stone2 mb-2"><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ochre">Licence conditions: </span>{o.licence_conditions}</p>
              <p className="text-sm text-stone2 mb-4"><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ochre">Cultural restrictions: </span>{o.cultural_restrictions}</p>
              <p className="font-mono text-[10px] text-stone2 mb-5">{o.rights_notice}</p>
              {o.status !== "Expired" && (
                <button onClick={() => download(o)} data-testid={`download-output-${i}`} className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 flex items-center gap-2">
                  <Download size={14} /> {o.acknowledged ? "Download" : "Review licence & download"}
                </button>
              )}
            </article>
          ))}
        </div>
      )}

      {ackFor && (
        <div className="fixed inset-0 z-50 bg-ink/85 flex items-center justify-center px-4" data-testid="licence-modal">
          <div className="w-full max-w-lg border border-ember/50 bg-surface1 p-7 md:p-9">
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck size={20} className="text-ember" />
              <h3 className="font-serif text-xl">Output licence notice</h3>
            </div>
            <div className="text-sm text-stone2 leading-relaxed space-y-3 mb-6" data-testid="licence-notice-text">
              <p>This output is licensed only for the recorded organisation, engagement and permitted purpose.</p>
              <p>Access to this output does not disclose, transfer or license the Bigibila codebook, methodology, analytical instruments, decision rules, scoring architecture, system logic or other internal intellectual property.</p>
              <p>The recipient must not reverse engineer the output to reproduce or derive Bigibila's methodology.</p>
            </div>
            <label className="flex items-start gap-3 text-sm text-bone cursor-pointer mb-6" data-testid="licence-agree-label">
              <input type="checkbox" data-testid="licence-agree-checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-[#B44A2C]" />
              I acknowledge and accept these licence conditions on behalf of my organisation.
            </label>
            <div className="flex gap-3">
              <button onClick={confirmAck} disabled={!agree} data-testid="licence-confirm-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-6 py-3 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-40">
                Acknowledge & download
              </button>
              <button onClick={() => setAckFor(null)} data-testid="licence-cancel-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-6 py-3 border border-line text-stone2 hover:text-bone transition-colors duration-300">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
